import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  Box,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const MotivosTable = ({
  motivos,
  loading,
  searchTerm,
  orderBy,
  orderDirection,
  handleSort,
  openEditDialog,
  handleDeleteMotivo,
  onToggleAtivo,
  onToggleJustificativa,
  isDarkMode,
  customTheme
}) => {
  // Estados para os diálogos de confirmação
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '', // 'ativo' ou 'justificativa'
    motivo: null,
    newValue: false,
    title: '',
    message: '',
    action: null
  });
  // Função para filtrar motivos baseado no termo de pesquisa
  const filteredMotivos = motivos.filter(motivo =>
    motivo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (motivo.descricao && motivo.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Função para ordenar motivos
  const sortedMotivos = [...filteredMotivos].sort((a, b) => {
    // 1. Primeiro critério: ativos antes de inativos
    if (a.ativo && !b.ativo) return -1;
    if (!a.ativo && b.ativo) return 1;
    
    // 2. Segundo critério: dentro do mesmo grupo (ativo/inativo), "Outro" sempre por último
    const aIsOutro = a.nome.toLowerCase() === 'outro';
    const bIsOutro = b.nome.toLowerCase() === 'outro';
    
    if (aIsOutro && !bIsOutro) return 1; // a vai para o final
    if (!aIsOutro && bIsOutro) return -1; // b vai para o final
    if (aIsOutro && bIsOutro) return 0; // ambos são "outro", manter ordem
    
    // 3. Terceiro critério: ordenação normal pelos campos
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Tratar valores nulos
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Converter para string para comparação
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();

    if (orderDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const handleSortClick = (column) => {
    handleSort(column);
  };

  // Função para lidar com clique na tag de status ativo
  const handleAtivoClick = (motivo) => {
    const newValue = !motivo.ativo;
    const action = newValue ? 'ativar' : 'desativar';
    
    setConfirmDialog({
      open: true,
      type: 'ativo',
      motivo,
      newValue,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Motivo`,
      message: newValue 
        ? `Ao ativar este motivo, os atendentes poderão selecioná-lo novamente na emissão de senhas.`
        : `Ao desativar este motivo, os atendentes não poderão selecioná-lo novamente, a menos que volte a ativar este motivo. Senhas já emitidas com este motivo não serão afetadas.`,
      action: () => onToggleAtivo(motivo.id, newValue)
    });
  };

  // Função para lidar com clique na tag de justificativa
  const handleJustificativaClick = (motivo) => {
    const newValue = !motivo.requer_justificativa;
    const action = newValue ? 'exigir' : 'remover a exigência de';
    
    setConfirmDialog({
      open: true,
      type: 'justificativa',
      motivo,
      newValue,
      title: `${newValue ? 'Exigir' : 'Remover'} Justificativa`,
      message: newValue 
        ? `Ao ativar esta opção, os atendentes precisarão fornecer uma justificativa adicional ao selecionar este motivo.`
        : `Ao desativar esta opção, os atendentes não precisarão mais fornecer justificativa ao selecionar este motivo.`,
      action: () => onToggleJustificativa(motivo.id, newValue)
    });
  };

  // Função para confirmar a ação
  const handleConfirmAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  // Função para cancelar a ação
  const handleCancelAction = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  if (loading) {
    return (
      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          borderRadius: 3,
          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          mt: 2
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {[...Array(5)].map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant="text" width="100%" height={24} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {[...Array(5)].map((_, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" width="100%" height={20} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (sortedMotivos.length === 0) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 6, 
          textAlign: 'center',
          borderRadius: 3,
          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          mt: 2,
          background: theme => theme.palette.mode === 'dark'
            ? alpha(customTheme.palette.grey[800], 0.3)
            : alpha(customTheme.palette.grey[50], 0.8)
        }}
      >
        <AssignmentIcon 
          sx={{ 
            fontSize: 64, 
            color: 'text.secondary',
            mb: 2
          }} 
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {searchTerm ? 'Nenhum motivo encontrado' : 'Nenhum motivo cadastrado'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {searchTerm 
            ? `Não foi possível encontrar motivos que correspondam a "${searchTerm}"`
            : 'Comece adicionando um novo motivo de emissão de senhas'
          }
        </Typography>
      </Paper>
    );
  }

  const tableContent = (
    <TableContainer 
      component={Paper} 
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
        mt: 2,
        overflow: 'hidden'
      }}
    >
      <Table>
        <TableHead>
          <TableRow 
            sx={{ 
              backgroundColor: theme => theme.palette.mode === 'dark'
                ? alpha(customTheme.palette.primary.dark, 0.2)
                : alpha(customTheme.palette.primary.main, 0.08)
            }}
          >
            <TableCell sx={{ fontWeight: 600, py: 2 }}>
              <TableSortLabel
                active={orderBy === 'nome'}
                direction={orderBy === 'nome' ? orderDirection : 'asc'}
                onClick={() => handleSortClick('nome')}
                sx={{
                  '&:hover': {
                    color: customTheme.palette.primary.main,
                  },
                  '&.Mui-active': {
                    color: customTheme.palette.primary.main,
                  },
                }}
              >
                Nome do Motivo
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }}>
              <TableSortLabel
                active={orderBy === 'descricao'}
                direction={orderBy === 'descricao' ? orderDirection : 'asc'}
                onClick={() => handleSortClick('descricao')}
                sx={{
                  '&:hover': {
                    color: customTheme.palette.primary.main,
                  },
                  '&.Mui-active': {
                    color: customTheme.palette.primary.main,
                  },
                }}
              >
                Descrição
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }} align="center">
              Requer Justificativa
            </TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }} align="center">
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, py: 2 }} align="center">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedMotivos.map((motivo, index) => (
            <TableRow 
              key={motivo.id}
              sx={{ 
                '&:hover': {
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? alpha(customTheme.palette.primary.main, 0.08)
                    : alpha(customTheme.palette.primary.main, 0.04),
                },
                '&:nth-of-type(even)': {
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? alpha(customTheme.palette.grey[800], 0.3)
                    : alpha(customTheme.palette.grey[50], 0.5),
                },
                transition: 'background-color 0.2s ease',
                // Estilo para motivos inativos
                ...((!motivo.ativo) && {
                  opacity: 0.3,
                  filter: 'grayscale(100)',
                  '&:hover': {
                    opacity: 0.4,
                  }
                }),
              }}
            >
              <TableCell sx={{ py: 2 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  {motivo.nome}
                </Typography>
              </TableCell>
              
              <TableCell sx={{ py: 2 }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {motivo.descricao || '-'}
                </Typography>
              </TableCell>

              <TableCell align="center" sx={{ py: 2 }}>
                <Tooltip title="Clique para alterar" placement="top">
                  <Chip
                    icon={motivo.requer_justificativa ? <CheckCircleIcon /> : <CancelIcon />}
                    label={motivo.requer_justificativa ? 'Sim' : 'Não'}
                    size="small"
                    color={motivo.requer_justificativa ? 'warning' : 'default'}
                    clickable
                    onClick={() => handleJustificativaClick(motivo)}
                    sx={{
                      fontWeight: 500,
                      minWidth: 80,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 2px 8px ${alpha(customTheme.palette.primary.main, 0.3)}`,
                      },
                      '& .MuiChip-icon': {
                        fontSize: 16
                      }
                    }}
                  />
                </Tooltip>
              </TableCell>

              <TableCell align="center" sx={{ py: 2 }}>
                <Tooltip title="Clique para alterar" placement="top">
                  <Chip
                    icon={motivo.ativo ? <CheckCircleIcon /> : <CancelIcon />}
                    label={motivo.ativo ? 'Ativo' : 'Inativo'}
                    size="small"
                    color={motivo.ativo ? 'success' : 'error'}
                    clickable
                    onClick={() => handleAtivoClick(motivo)}
                    sx={{
                      fontWeight: 500,
                      minWidth: 80,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 2px 8px ${alpha(customTheme.palette.primary.main, 0.3)}`,
                      },
                      '& .MuiChip-icon': {
                        fontSize: 16
                      }
                    }}
                  />
                </Tooltip>
              </TableCell>

              <TableCell align="center" sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title="Editar motivo" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(motivo)}
                      sx={{
                        color: customTheme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(customTheme.palette.primary.main, 0.1),
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Excluir motivo" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMotivo(motivo.id)}
                      sx={{
                        color: customTheme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(customTheme.palette.error.main, 0.1),
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      {tableContent}
      
      {/* Diálogo de confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme => theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
              : 'background.paper',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            background: theme => theme.palette.mode === 'dark'
              ? `linear-gradient(90deg, ${alpha(customTheme.palette.warning.dark, 0.3)} 0%, ${alpha(customTheme.palette.warning.main, 0.2)} 100%)`
              : `linear-gradient(90deg, ${alpha(customTheme.palette.warning.main, 0.08)} 0%, ${alpha(customTheme.palette.warning.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${alpha(customTheme.palette.warning.main, 0.1)}`,
          }}
        >
          <WarningIcon sx={{ color: customTheme.palette.warning.main, fontSize: 28 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              background: `linear-gradient(90deg, ${customTheme.palette.warning.main} 0%, ${customTheme.palette.warning.dark} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {confirmDialog.title}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: isDarkMode ? alpha(customTheme.palette.info.main, 0) : alpha(customTheme.palette.info.main, 0.08),
              border: `1px solid ${alpha(customTheme.palette.info.main, 0.2)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mt: 2
            }}
          >
            <InfoIcon sx={{ color: customTheme.palette.info.main, fontSize: 20, mt: 0.2 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Motivo: <strong>{confirmDialog.motivo?.nome}</strong>
              </Typography>
              <DialogContentText sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {confirmDialog.message}
              </DialogContentText>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            gap: 1,
            background: alpha(customTheme.palette.background.paper, 0.5),
            borderTop: `1px solid ${alpha(customTheme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={handleCancelAction}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: alpha(customTheme.palette.text.secondary, 0.3),
              color: 'text.secondary',
              '&:hover': {
                borderColor: customTheme.palette.text.secondary,
                backgroundColor: alpha(customTheme.palette.text.secondary, 0.08),
              },
            }}
          >
            Cancelar
          </Button>
          
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog.type === 'ativo' ? (confirmDialog.newValue ? 'success' : 'error') : 'warning'}
            sx={{
              borderRadius: 2,
              px: 3,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme => `0 4px 12px ${alpha(theme.palette[confirmDialog.type === 'ativo' ? (confirmDialog.newValue ? 'success' : 'error') : 'warning'].main, 0.4)}`,
              },
              transition: 'all 0.2s ease',
            }}
          >
            {confirmDialog.type === 'ativo' 
              ? (confirmDialog.newValue ? 'Ativar' : 'Desativar')
              : (confirmDialog.newValue ? 'Exigir Justificativa' : 'Remover Exigência')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MotivosTable; 